/**
 * Asset Precision Heuristics
 *
 * Pure display-formatting helpers. Decimal precision and trailing-zero rules
 * derived from the asset's classification — NO hardcoded per-symbol registry.
 *
 * Why heuristic and not DB-driven:
 *  - `displayDecimals`, `minSignificantDecimals`, `stripTrailingZeros` are
 *    UI presentation rules, not domain data (the DB owns `decimals` /
 *    `type` / `category`, which is what these heuristics derive from).
 *  - Keeping this purely client-side avoids a network round-trip for every
 *    `formatNumber()` call in `user-config.service.ts`.
 *  - Callers that DO have a fetched Asset row can pass the explicit type
 *    via `classifyAssetType()` to bypass symbol-pattern detection.
 */

import {
  deriveCryptoAssetDisplayPrecision,
  DEFAULT_CRYPTO_ASSET_DISPLAY_PRECISION,
} from '@bat/shared'

export enum AssetType {
  CRYPTO = 'crypto',
  FIAT = 'fiat',
  STABLECOIN = 'stablecoin',
  UNKNOWN = 'unknown',
}

export interface AssetMetadata {
  type: AssetType
  displayDecimals: number
  minSignificantDecimals: number
  stripTrailingZeros: boolean
}

/**
 * ISO-4217 fiat tickers that we render at zero decimals (no minor units).
 * Kept tiny and well-known: JPY, KRW, IDR, VND, ISK, CLP, HUF (rounded).
 * The set is purely a display heuristic — adding a new zero-decimal fiat
 * is one-line edit, not a schema change.
 */
const ZERO_DECIMAL_FIAT_TICKERS: ReadonlySet<string> = new Set([
  'JPY',
  'KRW',
  'IDR',
  'VND',
  'ISK',
  'CLP',
  'HUF',
])

/**
 * Common ISO-4217 fiat tickers used to short-circuit type detection when
 * the caller doesn't already know the asset's `type`. Extending this list
 * never breaks correctness: assets missing here just fall through to
 * UNKNOWN and use the safe default formatting (6 decimals, strip zeros).
 *
 * Source: ISO-4217 (subset of currencies BAT can price via ECB / fiat APIs).
 */
const KNOWN_FIAT_TICKERS: ReadonlySet<string> = new Set([
  'EUR',
  'USD',
  'GBP',
  'JPY',
  'CHF',
  'AUD',
  'CAD',
  'CNY',
  'HKD',
  'SGD',
  'SEK',
  'NOK',
  'DKK',
  'PLN',
  'MXN',
  'BRL',
  'INR',
  'KRW',
  'IDR',
  'VND',
  'ISK',
  'CLP',
  'HUF',
  'CZK',
  'TRY',
  'ZAR',
  'NZD',
  'ILS',
  'AED',
])

/**
 * Common stablecoin tickers used for symbol-pattern detection. Stablecoins
 * format like 6-decimal crypto but with a 2-decimal minimum (so $1.00 prints
 * as "1.00", not "1"). Extending this set has no correctness impact — assets
 * not listed here fall through to plain CRYPTO formatting.
 */
const KNOWN_STABLECOIN_TICKERS: ReadonlySet<string> = new Set([
  'USDT',
  'USDC',
  'DAI',
  'BUSD',
  'TUSD',
  'USDP',
  'GUSD',
  'FRAX',
  'LUSD',
  'EURS',
  'EURT',
  'EURC',
])

/**
 * Well-known major crypto tickers used to recover CRYPTO classification when
 * the caller cannot pass `knownType` (e.g. `user-config.service.formatNumber`
 * runs as a singleton outside React and has no access to the
 * `useGlobalAssets()` hook cache).
 *
 * Backend `bat.assets.{type,decimals}` is the canonical source. This set is
 * the FALLBACK for the major cryptos rendered in tables/lists before
 * per-asset metadata is fetched. Adding/removing entries never breaks
 * correctness — assets missing here fall through to UNKNOWN and use the
 * safe 6-decimal default. Extending requires no schema change.
 *
 * Tickers chosen as the majors whose precision overrides matter when the
 * caller cannot resolve `knownType` synchronously.
 */
const KNOWN_MAJOR_CRYPTO_TICKERS: ReadonlySet<string> = new Set([
  'BTC',
  'ETH',
  'BNB',
  'LINK',
  'UNI',
  'AAVE',
  'WETH',
  'SOL',
  'DOT',
  'ADA',
  'MATIC',
  'AVAX',
  'ATOM',
  'LTC',
  'XRP',
  'TRX',
])

/**
 * Decimales NATIVOS de las cadenas cuyos tickers este servicio tiene que
 * resolver sin React — es decir, sin poder leer `bat.assets.decimals`. Misma
 * lógica de FALLBACK que `KNOWN_MAJOR_CRYPTO_TICKERS`: la columna del catálogo
 * es la canónica, y estas entradas son su copia para el arranque en frío.
 *
 * ⚠️ SON DECIMALES DE CADENA, NO DECIMALES DE PANTALLA (#2234). Antes esta
 * tabla escribía directamente el `displayDecimals` final, y por eso podía
 * discrepar del backend sin que nada lo notase: el mismo SOL salía con 9
 * decimales aquí y con 8 en `GET /api/balances`. Ahora se declara la unidad de
 * la cadena y la pantalla la deriva `deriveCryptoAssetDisplayPrecision`, la
 * MISMA función de `@bat/shared` que usa el registro del backend.
 *
 *   SOL  9  → lamport   (1 SOL = 10^9 lamports)
 *   DOT 10  → planck    (1 DOT = 10^10 plancks)
 *   ADA  6  → lovelace  (1 ADA = 10^6 lovelaces)
 *
 * ⚠️ ADA con 6 parece un error al lado de BTC/ETH con 8, y en la misma columna
 * de una tabla se ve así: «0,02500000 BTC» junto a «850,000000 ADA» (medido en
 * la previsualización del importador, 2026-08-12). NO lo «normalices» a 8: ADA
 * no puede tener un séptimo decimal con valor, así que rellenar dos ceros más
 * inventaría una precisión que la cadena no ofrece.
 */
const CRYPTO_TICKERS_NATIVE_DECIMALS: Readonly<Record<string, number>> = {
  SOL: 9,
  DOT: 10,
  ADA: 6,
}

/**
 * Mínimo de decimales significativos de los tickers de arriba. Va aparte de los
 * decimales de cadena porque responde a otra pregunta —cuántos ceros de relleno
 * se conservan al quitar cola— y no se deriva de la unidad nativa.
 */
const CRYPTO_TICKERS_MIN_SIGNIFICANT_DECIMALS: Readonly<Record<string, number>> = {
  SOL: 6,
  DOT: 6,
  ADA: 6,
}

/**
 * Ceros de relleno que conserva un cripto sin entrada propia arriba. Coincide
 * en valor con el defecto de display, pero responde a otra pregunta, así que se
 * escribe aparte: si uno de los dos se mueve, el otro no debe seguirle.
 */
const DEFAULT_CRYPTO_MIN_SIGNIFICANT_DECIMALS = 8

/**
 * Default metadata for unknown assets — conservative middle ground.
 * Used when the symbol is null/empty or doesn't match any heuristic.
 */
const DEFAULT_METADATA: AssetMetadata = {
  type: AssetType.UNKNOWN,
  displayDecimals: 6,
  minSignificantDecimals: 2,
  stripTrailingZeros: true,
}

/**
 * Classify an asset purely from its ticker symbol. Used as a fallback when
 * the caller doesn't already know the asset's `type` field.
 *
 * Returns `AssetType.UNKNOWN` for symbols that don't match any heuristic,
 * letting the caller render with safe defaults instead of guessing wrong.
 *
 * @param normalizedSymbol - Uppercase, trimmed ticker (e.g. "BTC", "USDT")
 */
function classifyAssetType(normalizedSymbol: string): AssetType {
  if (KNOWN_FIAT_TICKERS.has(normalizedSymbol)) return AssetType.FIAT
  if (KNOWN_STABLECOIN_TICKERS.has(normalizedSymbol)) return AssetType.STABLECOIN
  // Well-known major cryptos that won't get a knownType from the caller.
  // Recognise BTC/ETH/SOL/DOT/etc. as CRYPTO so tables render with
  // high-precision formatting before useGlobalAssets() resolves.
  if (KNOWN_MAJOR_CRYPTO_TICKERS.has(normalizedSymbol)) return AssetType.CRYPTO
  // No reliable symbol pattern for the long tail of crypto — let the caller
  // decide via explicit `type` if they have it. Otherwise fall through to
  // UNKNOWN.
  return AssetType.UNKNOWN
}

/**
 * Build the display metadata for a given asset type + symbol. Pure function
 * — no I/O, no closure over global registries.
 */
function buildMetadataForType(type: AssetType, normalizedSymbol: string): AssetMetadata {
  switch (type) {
    case AssetType.FIAT: {
      const isZeroDecimal = ZERO_DECIMAL_FIAT_TICKERS.has(normalizedSymbol)
      const decimals = isZeroDecimal ? 0 : 2
      return {
        type: AssetType.FIAT,
        displayDecimals: decimals,
        minSignificantDecimals: decimals,
        stripTrailingZeros: false,
      }
    }
    case AssetType.STABLECOIN:
      return {
        type: AssetType.STABLECOIN,
        displayDecimals: 6,
        minSignificantDecimals: 2,
        stripTrailingZeros: true,
      }
    case AssetType.CRYPTO: {
      // Los decimales de pantalla NO se escriben aquí: salen de la unidad
      // nativa de la cadena pasada por la regla compartida (#2234), la misma
      // que deriva el `precision` que la pantalla de saldos recibe de la API.
      // De un ticker sin entrada no conocemos la cadena, así que no hay nada
      // que derivar: va al defecto cripto.
      const nativeDecimals = CRYPTO_TICKERS_NATIVE_DECIMALS[normalizedSymbol]
      return {
        type: AssetType.CRYPTO,
        displayDecimals:
          nativeDecimals === undefined
            ? DEFAULT_CRYPTO_ASSET_DISPLAY_PRECISION
            : deriveCryptoAssetDisplayPrecision(nativeDecimals),
        minSignificantDecimals:
          CRYPTO_TICKERS_MIN_SIGNIFICANT_DECIMALS[normalizedSymbol] ??
          DEFAULT_CRYPTO_MIN_SIGNIFICANT_DECIMALS,
        stripTrailingZeros: true,
      }
    }
    case AssetType.UNKNOWN:
    default:
      return DEFAULT_METADATA
  }
}

/**
 * Get display metadata for an asset.
 *
 * @param asset - Asset symbol (case-insensitive), e.g., "BTC", "EUR", "usdt"
 * @param knownType - Optional explicit type if caller already has a fetched
 *   Asset row from the API. When omitted, type is inferred from the symbol.
 *
 * @example
 * getAssetMetadata('BTC')
 * // → { type: 'crypto', displayDecimals: 8, ... }
 * //   (BTC is in KNOWN_MAJOR_CRYPTO_TICKERS, so the fallback heuristic
 * //    classifies it as CRYPTO even without an explicit knownType.)
 *
 * getAssetMetadata('SOL')
 * // → { type: 'crypto', displayDecimals: 9, minSignificantDecimals: 6, ... }
 * //   (unidad nativa de la cadena en CRYPTO_TICKERS_NATIVE_DECIMALS, derivada)
 *
 * getAssetMetadata('OBSCURE_ALT')
 * // → { type: 'unknown', displayDecimals: 6, ... }
 * //   (long-tail crypto not in KNOWN_MAJOR_CRYPTO_TICKERS — pass
 * //    `knownType: AssetType.CRYPTO` for full 8-decimal precision.)
 *
 * getAssetMetadata('EUR')
 * // → { type: 'fiat', displayDecimals: 2, ... }
 *
 * getAssetMetadata('JPY')
 * // → { type: 'fiat', displayDecimals: 0, ... }
 *
 * getAssetMetadata('USDT')
 * // → { type: 'stablecoin', displayDecimals: 6, ... }
 */
/**
 * ⚠️⚠️ Precisión para un ticker que el catálogo NO reconoce, en pantallas que
 * muestran CANTIDADES DE ACTIVO.
 *
 * `classifyAssetType` solo conoce fiat, stablecoins y las criptos mayores; toda
 * la cola larga cae en UNKNOWN, que se muestra con 6 decimales. En una app de
 * contabilidad cripto ese ticker es una cripto, nunca una divisa, así que
 * redondearlo a 6 borra dígitos reales de una cantidad.
 *
 * Medido en el navegador (2026-08-12), MISMA pantalla, mismo importe:
 *
 *   cabecera del modal de transacción → `0.12345678 QQQFIXITCOIN`
 *   editor de Valor Fiat, «Monto:»    → `0,123457`      ← redondeado
 *
 * Y ese editor es donde el usuario le pone precio a esa cantidad.
 *
 * Se aplica SOLO cuando el catálogo no sabe nada: un fiat o una stablecoin
 * reconocidos conservan su regla —forzar cripto le pondría 8 decimales a los
 * euros—. Mismo criterio que #1330 llevó a la rejilla y #1348 a la
 * previsualización del importador; vive aquí para que no vuelva a haber tres
 * copias que puedan divergir.
 */
export function longTailCryptoPrecision(
  asset: string | null | undefined,
): { knownType: AssetType } | undefined {
  return getAssetMetadata(asset).type === AssetType.UNKNOWN
    ? { knownType: AssetType.CRYPTO }
    : undefined
}

export function getAssetMetadata(
  asset: string | null | undefined,
  knownType?: AssetType,
): AssetMetadata {
  if (!asset) return DEFAULT_METADATA

  const normalized = asset.toUpperCase().trim()
  if (!normalized) return DEFAULT_METADATA

  const type = knownType ?? classifyAssetType(normalized)
  return buildMetadataForType(type, normalized)
}
